import { getVulnerabilities } from '../utils/getVulnerabilities';
import useAsync from 'react-use/esm/useAsync';
import { Team, VulnerabilityItem } from '@internal/schemas-common';
import { discoveryApiRef, useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useEffect, useRef } from 'react';

type UseGetVulnerabilitiesProps = { 
  teams?: Team[]; 
  useFullArrayAsDependency?: boolean;
};

type UseGetVulnerabilitiesResult = {
  vulnerabilities: VulnerabilityItem[] | undefined;
  loading: boolean;
  error: Error | undefined;
};

export const useGetVulnerabilities = ({
  teams
}: UseGetVulnerabilitiesProps): UseGetVulnerabilitiesResult => {
  const vulnerabilitiesBackendBaseurl = useRef('');
  const token = useRef('');
  const vulnerabilities = useRef<VulnerabilityItem[]>([]);
  const apiRef = useApi(discoveryApiRef);
  const identityRef = useApi(identityApiRef);
  const teamsIdentityKey = JSON.stringify(teams);

  useEffect(() => {
    identityRef.getCredentials().then(cred => {
      if (cred?.token) {
        token.current = cred.token;
      }
    });
  }, [identityRef]);

  // Get zf-vulnerabilities plugin url
  useEffect(() => {
    apiRef.getBaseUrl('zf-vulnerabilities').then(baseUrl => {
      if (baseUrl) {
        vulnerabilitiesBackendBaseurl.current = baseUrl;
      }
    });
  }, [apiRef]);

  const { value, loading, error } = useAsync(async () => {
    const teamNames = (!teams || teams.length === 0)
      ? ['ALL']
      : teams.slice().sort((a, b) => a.nameInGitlab.localeCompare(b.nameInGitlab)).map(t => t.nameInGitlab);
      
    // Get vulnerabilities from zf-vulnerabilities backend plugin
    const _vulnerabilities = await Promise.allSettled<VulnerabilityItem[]>(
      teamNames.map(teamName =>
        getVulnerabilities({
          baseUrl: vulnerabilitiesBackendBaseurl.current,
          token: token.current,
          teamName,
        }),
      ),
    );
    // TODO: log failures
    return _vulnerabilities
      .filter(_vulnerability => _vulnerability?.status === 'fulfilled')
      .map(
        _vulnerability =>
          (_vulnerability as PromiseFulfilledResult<VulnerabilityItem[]>).value,
      )
      .flat();
  }, [
    vulnerabilitiesBackendBaseurl.current, 
    token.current, 
    teamsIdentityKey,
  ])

  if (value) {
    vulnerabilities.current = value as VulnerabilityItem[];
  }

  return { vulnerabilities: vulnerabilities.current, loading, error };
};
